<template>
  <form class="submit-form" @submit.prevent="submit">
    <div class="field-block">
      <label class="block-label">{{ $t('support_form_type_label') }}</label>
      <div class="type-grid">
        <button
          v-for="opt in typeOptions"
          :key="opt.value"
          type="button"
          class="category-button"
          :class="{ active: form.type === opt.value }"
          @click="form.type = opt.value"
        >
          <i>{{ typeCode(opt) }}</i>
          <span>
            <strong>{{ typeLabel(opt) }}</strong>
            <small>{{ typeHint(opt) }}</small>
          </span>
        </button>
      </div>
    </div>

    <div class="field-block message-field">
      <div class="field-label-row">
        <label class="block-label">{{ $t('support_form_desc_label') }}</label>
        <span>{{ descLength }}/600</span>
      </div>
      <el-input
        v-model="form.description"
        type="textarea"
        maxlength="600"
        :rows="5"
        :placeholder="$t('support_form_desc_placeholder')"
        class="message-input"
      />
    </div>

    <div class="field-block upload-field">
      <div class="field-label-row">
        <label class="block-label">{{ $t('support_form_upload_label') }}</label>
        <span>{{ $t('support_form_upload_tips') }}</span>
      </div>
      <div class="upload-strip">
        <el-upload
          class="upload-tile"
          multiple
          :limit="5"
          :file-list="fileList"
          :auto-upload="false"
          :show-file-list="false"
          :on-change="handleFileChange"
          :on-remove="handleFileRemove"
          :before-upload="beforeUpload"
          action="/"
          :http-request="noopRequest"
        >
          <span class="image-icon" aria-hidden="true"></span>
          <span>{{ $t('support_form_upload_click') }}</span>
        </el-upload>

        <div class="attachment-list" v-if="fileList.length">
          <div
            v-for="file in fileList"
            :key="file.uid || file.name"
            class="attachment-card"
            :class="{ uploading: file.__uploading }"
          >
            <img :src="getFileThumbUrl(file)" :alt="$t('support_attachment_preview')">
            <button class="remove-attachment" type="button" :aria-label="$t('support_remove_attachment')" @click="removeFile(file)">×</button>
          </div>
        </div>
      </div>
    </div>

    <div class="form-actions">
      <button class="ghost-button" type="button" @click="resetForm">
        {{ $t('cancel_text') }}
      </button>
      <button class="primary-button" type="submit">
        {{ $t('support_form_submit') }}
      </button>
    </div>

    <el-dialog :visible.sync="previewVisible" append-to-body :width="isMobile ? '100%' : '50%'">
      <div class="preview-container">
        <img v-if="previewSrc" :src="previewSrc" class="preview-image" :alt="$t('support_attachment_preview')" />
      </div>
    </el-dialog>
  </form>
</template>

<script>
import { uploadMedia } from '@/utils/uploadService.js'

export default {
  name: 'SupportForm',
  props: {
    typeOptions: {
      type: Array,
      default: () => []
    },
    defaultType: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      form: {
        type: '',
        description: ''
      },
      fileList: [],
      previewVisible: false,
      previewSrc: '',
      isMobile: false
    }
  },
  computed: {
    descLength() {
      return (this.form.description || '').length
    }
  },
  watch: {
    defaultType: {
      handler(val) {
        this.form.type = val || this.form.type || (this.typeOptions[0] && this.typeOptions[0].value) || ''
      },
      immediate: true
    }
  },
  mounted() {
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile)
  },
  methods: {
    typeLabel(opt) {
      return this.$t(opt.labelKey || opt.label || '') || opt.label || '-'
    },
    typeCode(opt) {
      const map = {
        RECHARGE: 'support_type_topup_code',
        ACCOUNT: 'support_type_account_code',
        GUIDE: 'support_type_guide_code',
        SUGGESTION: 'support_type_suggestion_code',
        REPORT: 'support_type_report_code',
        OTHER: 'support_type_other_code'
      }
      return this.$t(map[String(opt.value).toUpperCase()] || 'support_type_other_code')
    },
    typeHint(opt) {
      const map = {
        RECHARGE: 'support_type_topup_hint',
        ACCOUNT: 'support_type_account_hint',
        GUIDE: 'support_type_guide_hint',
        SUGGESTION: 'support_type_suggestion_hint',
        REPORT: 'support_type_report_hint',
        OTHER: 'support_type_other_hint'
      }
      return this.$t(map[String(opt.value).toUpperCase()] || 'support_type_other_hint')
    },
    beforeUpload(file) {
      const isImageOrVideo = file.type.startsWith('image/') || file.type.startsWith('video/')
      if (!isImageOrVideo) return true

      const maxSizeMB = file.type.startsWith('image/') ? 10 : 50
      const ok = file.size / 1024 / 1024 <= maxSizeMB
      if (!ok) {
        this.$message.error(
          file.type.startsWith('image/')
            ? this.$t('support_form_image_too_big')
            : this.$t('support_form_video_too_big')
        )
      }
      return ok
    },
    handleFileChange(file, fileList) {
      if (!file) return
      if (file.__uploading || file.__uploaded) {
        this.fileList = fileList
        return
      }

      file.__uploading = true
      file.status = 'uploading'
      uploadMedia(file, { space: 'cs', isReduce: true })
        .then(({ url, isVideo }) => {
          file.url = url
          file.__isVideo = !!isVideo
          file.__uploaded = true
          file.status = 'success'
        })
        .catch(err => {
          file.status = 'fail'
          const isTooLarge = err && err.code === 'FILE_TOO_LARGE'
          const msg = isTooLarge
            ? (err.isVideo ? this.$t('support_form_video_too_big') : this.$t('support_form_image_too_big'))
            : this.$t('support_send_fail')
          this.$message.error(msg)
        })
        .finally(() => {
          file.__uploading = false
          this.fileList = fileList
        })
    },
    handleFileRemove(file, fileList) {
      this.fileList = fileList
    },
    getFileUrl(file) {
      return file.url || (file.raw ? URL.createObjectURL(file.raw) : '')
    },
    getFileThumbUrl(file) {
      const type = file && file.type ? String(file.type).toLowerCase() : ''
      if (type.startsWith('video/') || file.__isVideo) {
        return '/static/image/video.png'
      }
      return this.getFileUrl(file)
    },
    removeFile(file) {
      this.fileList = this.fileList.filter(item => item.uid !== file.uid)
    },
    resetForm() {
      this.form.description = ''
      this.fileList = []
      this.$emit('cancel')
    },
    submit() {
      if (!this.form.type) {
        this.$message.warning(this.$t('support_form_type_placeholder'))
        return
      }
      if (!this.form.description || !this.form.description.trim()) {
        this.$message.warning(this.$t('support_form_desc_placeholder'))
        return
      }
      if (this.fileList.some(file => file.__uploading)) {
        this.$message.warning(this.$t('support_submitting'))
        return
      }

      this.$emit('submit', {
        type: this.form.type,
        description: this.form.description.trim(),
        files: this.fileList
      })
    },
    checkMobile() {
      this.isMobile = window.innerWidth <= 768
    },
    noopRequest() {
      return Promise.resolve()
    }
  }
}
</script>

<style scoped>
.submit-form {
  display: flex;
  min-height: 0;
  flex-direction: column;
  gap: 14px;
}

.field-block {
 border: 1px solid var(--support-line);
 border-radius: 8px;
  padding: 16px;
 background: #fff;
}

.block-label,
.field-label-row label {
  color: var(--support-text);
  font-size: .175rem;
  font-weight: 700;
  line-height: 24px;
}

.field-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 9px;
}

.field-label-row span {
  color: var(--support-muted);
  font-size: .175rem;
  line-height: 20px;
}

.type-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-top: 12px;
}

.category-button {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 62px;
  border: 1px solid var(--support-line);
  border-radius: 8px;
  padding: 10px;
  color: var(--support-text);
  text-align: left;
  background: #fff;
  cursor: pointer;
  transition: border-color .16s ease, background .16s ease;
}

.category-button i {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  color: #fff;
  font-size: .175rem;
  font-style: normal;
  font-weight: 800;
  line-height: 1;
  background: var(--support-primary);
}

.category-button > span {
  display: block;
  min-width: 0;
}

.category-button strong {
  display: block;
  overflow: hidden;
  font-size: .175rem;
  line-height: 22px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.category-button small {
  display: block;
  margin-top: 1px;
  overflow: hidden;
  color: var(--support-muted);
  font-size: .175rem;
  font-weight: 400;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.category-button:hover,
.category-button.active {
  border-color: var(--support-primary);
  background: var(--support-primary-soft);
}

::v-deep .message-input .el-textarea__inner {
  height: 132px;
  min-height: 132px !important;
  border: 1px solid #d4deeb;
  border-radius: 8px;
  padding: 13px 14px;
  color: var(--support-text);
  font-size: .175rem;
  line-height: 24px;
  resize: none;
  background: #fbfdff;
}

::v-deep .message-input .el-textarea__inner:focus {
  border-color: var(--support-primary);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--support-primary) 14%, transparent);
}

.upload-strip {
  display: flex;
  align-items: stretch;
  gap: 10px;
  min-height: 68px;
}

.upload-tile {
  flex: 0 0 128px;
}

::v-deep .upload-tile .el-upload {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 5px;
  width: 128px;
  height: 78px;
  border: 1px dashed var(--support-primary-line);
  border-radius: 8px;
  color: var(--support-primary-strong);
  background: #fff;
}

::v-deep .upload-tile .el-upload:hover {
  border-color: var(--support-primary);
  background: var(--support-primary-soft);
}

.image-icon {
  position: relative;
  display: inline-flex;
  width: 26px;
  height: 26px;
  border: 1px solid currentColor;
  border-radius: 7px;
  color: var(--support-primary);
  background: rgba(255, 255, 255, .52);
}

.image-icon::before {
  content: "";
  position: absolute;
  inset: 5px;
  border: 2px solid currentColor;
  border-radius: 50%;
}

.attachment-list {
  display: flex;
  min-width: 0;
  flex: 1;
  gap: 8px;
  overflow-x: auto;
}

.attachment-card {
  position: relative;
  flex: 0 0 72px;
  width: 72px;
  height: 68px;
  overflow: hidden;
  border: 1px solid var(--support-line);
  border-radius: 8px;
  background: var(--support-bg);
}

.attachment-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.attachment-card.uploading::after {
  content: "";
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, .68);
}

.remove-attachment {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 20px;
  height: 20px;
  border: 0;
  border-radius: 50%;
  color: #fff;
  line-height: 20px;
  background: rgba(16, 28, 46, .68);
  cursor: pointer;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.primary-button,
.ghost-button {
  height: 44px;
  border: 0;
  border-radius: 8px;
  padding: 0 18px;
  font-size: .175rem;
  font-weight: 700;
  cursor: pointer;
}

.primary-button {
  min-width: 128px;
  color: #fff;
  background: var(--support-primary);
}

.ghost-button {
  min-width: 96px;
  border: 1px solid var(--support-line);
  color: #42526a;
  background: #fff;
}

.preview-container {
  width: 50vw;
  height: 50vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, .08);
}

.preview-image {
  max-width: 48vw;
  max-height: 48vh;
  object-fit: contain;
  border-radius: 6px;
}

@media (max-width: 768px) {
  .submit-form {
    position: relative;
    min-height: 100%;
    gap: .3rem;
    padding-bottom: 1.16rem;
  }

  .field-block {
    border-color: #d9e4f1;
    border-radius: .14rem;
    padding: .28rem;
    background: #fff;
  }

  .block-label,
  .field-label-row label {
    color: #13213a;
    font-size: .32rem;
    line-height: .42rem;
  }

  .type-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: .2rem;
    margin-top: .22rem;
  }

  .category-button {
    min-height: 1.12rem;
    gap: .16rem;
    padding: .14rem;
    border-color: #d9e4f1;
    border-radius: .14rem;
    background: #fff;
  }

  .category-button i {
    flex-basis: .68rem;
    width: .68rem;
    height: .68rem;
    border-radius: .14rem;
    font-size: .28rem;
  }

  .category-button strong {
    font-size: .3rem;
    line-height: .38rem;
  }

  .category-button small {
    margin-top: .02rem;
    font-size: .22rem;
    line-height: .3rem;
    white-space: normal;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
  }

  .field-label-row {
    margin-bottom: .18rem;
  }

  .field-label-row span {
    max-width: 4.2rem;
    color: #697891;
    font-size: .26rem;
    text-align: right;
    line-height: .34rem;
  }

  ::v-deep .message-input .el-textarea__inner {
    height: 2.6rem;
    min-height: 2.6rem !important;
    border-color: #d9e4f1;
    border-radius: .14rem;
    padding: .26rem;
    font-size: .32rem;
    line-height: .44rem;
    background: #fff;
  }

  .upload-strip {
    min-height: 1.28rem;
    align-items: flex-start;
  }

  .upload-tile {
    flex-basis: 2.08rem;
  }

  ::v-deep .upload-tile .el-upload {
    width: 2.08rem;
    height: 1.28rem;
    border-radius: .14rem;
    border-color: var(--support-primary-line);
    background: #fff;
  }

  ::v-deep .upload-tile .el-upload span:last-child {
    font-size: .3rem;
    line-height: .38rem;
  }

  .image-icon {
    width: .38rem;
    height: .38rem;
    border-radius: .1rem;
  }

  .image-icon::before {
    inset: .08rem;
    border-width: .04rem;
  }

  .form-actions {
    position: sticky;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 2;
    justify-content: flex-end;
    gap: .22rem;
    margin: 0 -.32rem -.18rem;
    padding: .22rem .32rem;
    border-top: 1px solid #e6edf6;
    background: #fff;
  }

  .primary-button,
  .ghost-button {
    flex: 0 0 2.16rem;
    min-width: 2.16rem;
    height: .96rem;
    border-radius: .16rem;
    padding: 0 .28rem;
    font-size: .34rem;
  }

  .ghost-button {
    margin-left: auto;
  }
}
</style>
